import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Mail, MapPin } from "lucide-react";

export default function AdminSampleRequests() {
  const [items, setItems] = useState([]);
  const load = async () => {
    try { const { data } = await api.get("/admin/sample-requests"); setItems(data); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = async (id, next) => {
    try {
      await api.post(`/admin/sample-requests/${id}/daily-email`, { enabled: next });
      setItems((xs) => xs.map((x) => x.id === id ? { ...x, daily_email_enabled: next } : x));
      toast.success(next ? "Daily emails enabled" : "Daily emails disabled");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
  };

  return (
    <div className="animate-fade-up">
      <div className="mb-6">
        <h1 className="font-heading font-bold text-3xl sm:text-4xl">Sample Requests</h1>
        <p className="mt-1.5 text-sm text-muted-foreground">Leads who downloaded the free sample. Toggle daily-count emails per lead.</p>
      </div>
      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Criteria</th>
                <th className="text-left px-4 py-3">Downloaded</th>
                <th className="text-left px-4 py-3">IP / UA</th>
                <th className="text-center px-4 py-3">Daily email</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-t border-border hover:bg-accent/40" data-testid={`sample-row-${r.id}`}>
                  <td className="px-4 py-3 num">
                    <div className="font-medium">{r.email}</div>
                    {r.auto_stopped && <Badge variant="outline" className="mt-1 text-[10px]">Auto-stopped (subscribed)</Badge>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      <Badge variant="secondary" className="uppercase text-[10px]">{r.dtype}</Badge>
                      <Badge variant="outline" className="text-[10px]">
                        <MapPin className="h-3 w-3 mr-1" />
                        {r.coverage === "state" ? r.state : "Pan India"}
                      </Badge>
                      <Badge variant="outline" className="text-[10px] num">{r.row_count || 0} rows</Badge>
                    </div>
                  </td>
                  <td className="px-4 py-3 num text-xs">{r.downloaded_at ? format(new Date(r.downloaded_at), "dd MMM yyyy · HH:mm") : "—"}</td>
                  <td className="px-4 py-3 num text-xs text-muted-foreground">
                    <div>{r.ip}</div>
                    <div className="truncate max-w-[240px]" title={r.user_agent}>{r.user_agent}</div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Switch
                      checked={!!r.daily_email_enabled}
                      onCheckedChange={(v) => toggle(r.id, v)}
                      data-testid={`sample-toggle-${r.id}`}
                    />
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-12 text-center text-sm text-muted-foreground">
                  <Mail className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  No sample downloads yet
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
